import { Provider } from "../global/Provider-Api";

const provider = new Provider();

/**
 * API client for the subscription signup flow.
 *
 * Pattern follows services/setting.js — a class with one method per endpoint,
 * exported as a singleton.
 *
 * Auth: most endpoints in this flow use the guest_token from sessionStorage.
 * The Provider-Api wrapper picks it up automatically when `token` is passed.
 */
class apiSubscription {
  // ────────────────────────────────────────────────────────────
  // Reference data — fetched on flow load
  // ────────────────────────────────────────────────────────────

  async getProjects() {
    const dataRes = await provider.api({
      type: "GET",
      url: "/subscriptionV2/projects",
    });
    return dataRes;
  }

  async getProjectPlans(value) {
    const dataRes = await provider.api({
      type: "GET",
      url: "/subscriptionV2/plans",
      data: { id_project: value.id_project },
    });
    return dataRes;
  }

  /**
   * Fetch Omise public_key for a project (eager-fetched on signup page mount).
   * Returns: { status, omise: { enabled, public_key, mode } }
   */
  async getPaymentConfig(value) {
    const dataRes = await provider.api({
      type: "GET",
      url: "/subscriptionV2/payment-config",
      data: { id_project: value.id_project },
    });
    return dataRes;
  }

  async getTerms() {
    const dataRes = await provider.api({
      type: "GET",
      url: "/subscriptionV2/terms",
    });
    return dataRes;
  }

  async getSubscriptionPlanDetail(value) {
    const dataRes = await provider.api({
      type: "GET",
      url: `/pkindev/subscription_plan/${value.id_subscription_plan_table}`,
    });
    return dataRes;
  }

  async getTermsCondition(value) {
    const dataRes = await provider.api({
      type: "GET",
      url: `/subscriptionV2/terms/${value.id_project}`,
    });
    return dataRes;
  }

  async getPrivacyPolicy(value) {
    const dataRes = await provider.api({
      type: "GET",
      url: `/subscriptionV2/privacy/${value.id_project}`,
    });
    return dataRes;
  }

  async getRefundablePolicy(value) {
    const dataRes = await provider.api({
      type: "GET",
      url: `/subscriptionV2/refund/${value.id_project}`,
    });
    return dataRes;
  }

  // ────────────────────────────────────────────────────────────
  // Pre-signup duplicate / identity checks
  // Used by AccountStep/CompanyStep to flag issues before submit.
  // ────────────────────────────────────────────────────────────

  /**
   * Check the central Bill_User identity (invoize.me login).
   * Used on AccountStep onBlur of the email field.
   *
   * Body: { email }
   * Returns one of:
   *   { status: "new" }
   *   { status: "existing_unverified", message }
   *   { status: "existing_verified", first_name }
   */
  async checkBillEmail(value) {
    const dataRes = await provider.api({
      type: "POST",
      url: "/subscriptionV2/check-bill-email",
      data: { email: value.email },
    });
    return dataRes;
  }

  /**
   * Check the project-specific email (e.g. LeaveHub.companies.hr_email).
   *
   * Phase 2 split this from the Bill_User email — they may be the
   * same value or different, but they're checked against different
   * tables. Backend expects field name `project_email`.
   *
   * Body: { project_email, id_project }
   * Returns:
   *   { ok: true }
   *   { ok: false, code, message, field }
   */
  async checkProjectEmail(value) {
    const dataRes = await provider.api({
      type: "POST",
      url: "/subscriptionV2/check-project-email",
      data: {
        project_email: value.project_email,
        id_project:    value.id_project,
      },
    });
    return dataRes;
  }

  /**
   * Check billing identity (CustomerInvoice tax/branch combo).
   * Used on CompanyStep onBlur of tax_number / name_branch.
   *
   * Body: { id_project, detail_type, tax_number, name_branch? }
   * Returns:
   *   { ok: true }
   *   { ok: false, code, message, field }
   */
  async checkBillingIdentity(value) {
    const dataRes = await provider.api({
      type: "POST",
      url: "/subscriptionV2/check-billing-identity",
      data: {
        id_project:  value.id_project,
        detail_type: value.detail_type,
        tax_number:  value.tax_number,
        name_branch: value.name_branch,
      },
    });
    return dataRes;
  }

  /**
   * Verify a tax number's format.
   * Debounced from CompanyStep while the user types.
   *
   * Server-side on purpose: today it only runs a checksum, but the rule
   * is expected to grow (Revenue Department TIN lookup) — keeping it on
   * the backend means the UI needs no change when that lands.
   *
   * Body: { tax_number, detail_type }
   * Returns:
   *   { valid: true }
   *   { valid: false, message }
   */
  async verifyTaxId(value) {
    const dataRes = await provider.api({
      type: "POST",
      url: "/subscriptionV2/verify-taxid",
      data: {
        tax_number:  value.tax_number,
        detail_type: value.detail_type,
      },
    });
    return dataRes;
  }

  // ────────────────────────────────────────────────────────────
  // Code lookup — debounced from UI
  // ────────────────────────────────────────────────────────────

  /**
   * Validate a promo code against the platform DB.
   * Backend returns { id_promo_code, code, discount_type, discount_value }
   * or { status: "error" } if not found.
   */
  async validatePromoCode(value) {
    const dataRes = await provider.api({
      type: "POST",
      url: "/subscription/promo/validate",
      data: value, // { code, id_project }
    });
    return dataRes;
  }

  /**
   * Validate a referral code against the referral table.
   * Backend looks up `referral_key`, returns { id_referral_key, id_user }
   * if found, or { status: "error" } if not.
   */
  async validateReferralCode(value) {
    const dataRes = await provider.api({
      type: "POST",
      url: "/subscriptionV2/referral/validate",
      data: { referral_key: value.code ?? value.referral_key },
    });
    return dataRes;
  }

  // ────────────────────────────────────────────────────────────
  // Atomic submit — replaces the legacy 3-call pipeline
  // ────────────────────────────────────────────────────────────

  /**
   * Create a full subscription in one atomic call.
   * Backend wraps Omise authorize → DB transaction (multi-schema) → Omise capture.
   * Either everything succeeds, or nothing is persisted and the card is not charged.
   *
   * Payload shape:
   *   {
   *     id_project, id_subscription_plan_table, omise_token,
   *     company:      { ... },
   *     organization: { ... } | null,
   *     account:      { first_name, last_name, email, password, ... },
   *     promo:        { id_promo_code } | null,
   *     referral:     { id_referral_key } | null,
   *   }
   *
   * Phase 2 — `options.token` (string, optional):
   *   When the user signed in via the modal in AccountStep, pass the
   *   accessToken returned by /auth_user. Provider-Api will forward
   *   it as `x-has-accesstoken`, and the backend's
   *   tryExtractExistingAuth() reads it to put the request into
   *   "existing user" mode (re-uses Bill_User, only adds a new
   *   BillAndBranch row instead of a full Bill ecosystem).
   *
   *   When omitted, the backend treats the request as a brand-new
   *   signup (no Bill_User yet) — the same path used before Phase 2.
   */
  async createSubscription(value, options = {}) {
    const dataRes = await provider.api({
      type: "POST",
      url: "/subscriptionV2/",
      data: value,
      token: options.token,
    });
    return dataRes;
  }

  // ────────────────────────────────────────────────────────────
  // Manage subscription (logged-in Bill_User)
  //
  // ทุก method ต้องใช้ accessToken ของ Bill_User (login แล้ว) —
  // ส่งผ่าน options.token → Provider-Api forward เป็น x-has-accesstoken.
  // backend verify id_user + ownership.
  //
  // id_subscription มาจาก invoice response (Bill_Subscription JOIN).
  // ────────────────────────────────────────────────────────────

  /**
   * ยกเลิกสมาชิก (commitment-aware) — ตั้ง canceled_at ล่วงหน้า ไม่ตัด schedule ทันที
   *
   * Body: { id_subscription }
   * Returns 200:
   *   { status:"success", canceled_at, remaining_periods, final_period, already_canceled }
   * Errors: 401 · 404 SUBSCRIPTION_NOT_FOUND · 409 SUBSCRIPTION_NOT_ACTIVE / PLAN_CHANGE_IN_PROGRESS
   */
  async cancelSubscription(value, options = {}) {
    const dataRes = await provider.api({
      type: "POST",
      url: "/subscriptionV2/cancel",
      data: { id_subscription: value.id_subscription },
      token: options.token,
    });
    return dataRes;
  }

  /**
   * ยกเลิกคำขอยกเลิก (คืนสถานะปกติ) — ทำได้ถ้ายังไม่ถึง canceled_at และ schedule ยังอยู่
   *
   * Body: { id_subscription }
   * Returns 200: { status:"success", canceled_at:null, undone:true }
   * Errors: 404 NO_PENDING_CANCEL · 409 CANCEL_ALREADY_EFFECTIVE / SCHEDULE_DESTROYED
   */
  async undoCancelSubscription(value, options = {}) {
    const dataRes = await provider.api({
      type: "POST",
      url: "/subscriptionV2/cancel/undo",
      data: { id_subscription: value.id_subscription },
      token: options.token,
    });
    return dataRes;
  }

  /**
   * Preview การเปลี่ยนแผน (dry-run) — ไม่ตัดเงิน
   *
   * Body: { id_subscription, id_subscription_plan_table }
   * Returns 200 (upgrade):   { kind:"upgrade", credit, charged, next_charge_at, ... }
   * Returns 200 (downgrade): { kind:"downgrade", plan_change_at, ... }
   */
  async previewPlanChange(value, options = {}) {
    const dataRes = await provider.api({
      type: "POST",
      url: "/subscriptionV2/change-plan/preview",
      data: {
        id_subscription: value.id_subscription,
        id_subscription_plan_table: value.id_subscription_plan_table,
      },
      token: options.token,
    });
    return dataRes;
  }

  /**
   * เปลี่ยนแผน — endpoint เดียว แยก upgrade/downgrade อัตโนมัติที่ backend
   *
   * Body: { id_subscription, id_subscription_plan_table }
   * Returns 200 (upgrade):   { kind:"upgrade", credit, charged, next_charge_at, ... }  ← ตัดบัตรแล้ว
   * Returns 200 (downgrade): { pending_plan_change, plan_change_at, ... }              ← รอ commitment
   * Errors: 400 SAME_PLAN/PLAN_NOT_ALLOWED · 402 CHARGE_FAILED · 409 หลายแบบ
   */
  async changePlan(value, options = {}) {
    const dataRes = await provider.api({
      type: "POST",
      url: "/subscriptionV2/change-plan",
      data: {
        id_subscription: value.id_subscription,
        id_subscription_plan_table: value.id_subscription_plan_table,
      },
      token: options.token,
    });
    return dataRes;
  }

  /**
   * ยกเลิกคำขอเปลี่ยนแผน (downgrade เท่านั้น — upgrade เปลี่ยนไปแล้ว undo ไม่ได้)
   *
   * Body: { id_subscription }
   * Returns 200: { status:"success", pending_plan_change:null, undone:true }
   * Errors: 404 NO_PENDING_PLAN_CHANGE · 409 PLAN_CHANGE_ALREADY_EFFECTIVE
   */
  async undoChangePlan(value, options = {}) {
    const dataRes = await provider.api({
      type: "POST",
      url: "/subscriptionV2/change-plan/undo",
      data: { id_subscription: value.id_subscription },
      token: options.token,
    });
    return dataRes;
  }

  /**
   * ดึงรายการบัตรทั้งหมดของ user (ทุก subscription) — หน้า Payment Setting
   *
   * Returns 200: { status:"success", data: [ { card_info, name_project, logo_icon, ... } ] }
   */
  async listPaymentMethods(options = {}) {
    const dataRes = await provider.api({
      type: "GET",
      url: "/subscriptionV2/payment-methods",
      token: options.token,
    });
    return dataRes;
  }

  /**
   * เปลี่ยนบัตรที่ผูกกับ subscription — เพิ่มบัตรใหม่เป็น default ใน Omise customer,
   * ลบบัตรเก่า, อัปเดต Bill_Payment. schedule ตัดบัตรใหม่อัตโนมัติ (default_card).
   *
   * รับได้ทั้ง id_subscription (หน้า contract) หรือ id_bill_branch (หน้า Payment).
   *
   * Body: { id_subscription? , id_bill_branch? , omise_token }
   * Returns 200: { status:"success", id_subscription, card_info, old_card_deleted }
   * Errors: 400 OMISE_TOKEN_INVALID · 404 SUBSCRIPTION_NOT_FOUND · 409 NO_CUSTOMER · 502
   */
  async updatePaymentMethod(value, options = {}) {
    const dataRes = await provider.api({
      type: "POST",
      url: "/subscriptionV2/payment-method",
      data: {
        ...(value.id_subscription ? { id_subscription: value.id_subscription } : {}),
        ...(value.id_bill_branch ? { id_bill_branch: value.id_bill_branch } : {}),
        omise_token: value.omise_token,
      },
      token: options.token,
    });
    return dataRes;
  }

  // ────────────────────────────────────────────────────────────
  // Existing user login (Phase 2)
  //
  // Hits the central /auth_user endpoint that already powers
  // invoize.me login. The response shape is unusual (top-level array
  // — see backend implementation), so callers should consume
  // dataRes[0] when reading status / token / id_bill.
  //
  // Used by AccountStep when the email field reports
  // billStatus="existing_verified" and the user clicks the small
  // "เข้าสู่ระบบ invoize.me" link to skip re-creating their Bill_User.
  // ────────────────────────────────────────────────────────────

  /**
   * Authenticate against an existing Bill_User account.
   *
   * Body: { email, password }
   * Returns (top-level array — index [0] holds the payload):
   *   [{ status: true,  id_user, id_bill, id_project, permission, accessToken }]  // success
   *   [{ status: false, accessToken: null }]                                       // wrong password / no user
   */
  async loginInvoize(value) {
    const dataRes = await provider.api({
      type: "POST",
      url: "/billing/user/auth_user",
      data: { email: value.email, password: value.password },
    });
    return dataRes;
  }

  // ────────────────────────────────────────────────────────────
  // Guest token lifecycle
  // ────────────────────────────────────────────────────────────

  async createGuestToken(value) {
    const dataRes = await provider.api({
      type: "POST",
      url: "/subscription/auth/create_token",
      data: value, // { id_project }
    });
    return dataRes;
  }

  async verifyGuestToken() {
    const dataRes = await provider.api({
      type: "GET",
      url: "/subscription/auth/verify_token",
    });
    return dataRes;
  }
}

export default new apiSubscription();